import React, { useState, useEffect } from "react";
import { useAppDispatch } from "../../redux/hooks";
import { setBreadcrumb } from "../../redux/slices/breadCrumb.slice";
import { notifications } from "@mantine/notifications";
import { useAuthenticator } from "@aws-amplify/ui-react";
import {
  useGetUsersQuery,
  useLazyGetUsersQuery,
  useGetRolesQuery,
  useCreateUserMutation,
  useEditUserMutation,
  useDeleteUserMutation,
  useLazySearchUsersQuery,
} from "../../redux/slices/api.slice";
import { TbDatabaseOff } from "react-icons/tb";
import { Formik } from "formik";
import { RiDeleteBin6Line } from "react-icons/ri";
import { FiEdit2 } from "react-icons/fi";
import { LuChevronsUpDown } from "react-icons/lu";
import { MdDeleteOutline } from "react-icons/md";
import TrashIcon from "../../svg/trashIcon";
import EditIcon from "../../svg/editIcon";
import {
  Input,
  Button,
  SegmentedControl,
  Modal,
  Select,
  InputBase,
  Table,
} from "@mantine/core";
import { IoSearchOutline } from "react-icons/io5";
import { MdAdd } from "react-icons/md";
import ExportIcon from "../../svg/exportIcon";
import CustomTable from "../../components/Table";
import { object } from "yup";
import PermissionAction from "../../components/permissions/PermissionAction";
import Container from "../../components/Container";
import DeleteModal from "../../components/modals/DeleteModal";
import {
  canManageUserRole,
  creatableUserRoleOptions,
  userRoleOptions,
} from "../../interfaces/roles";

function apiErrorMessage(error: any): string {
  return (
    error?.data?.title ||
    error?.error ||
    "Something went wrong. Please try again."
  );
}

const Users = () => {
  const { user } = useAuthenticator();
  const callerGroups =
    user?.getSignInUserSession?.()?.getAccessToken?.()?.decodePayload?.()?.[
      "cognito:groups"
    ];
  const creatableRoles = creatableUserRoleOptions(callerGroups);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const dispatch = useAppDispatch();
  const [
    createUsers,
    { isError: isErrorCreatedUser, isLoading: isLoadingCreateUser },
  ] = useCreateUserMutation();
  const [
    editUsers,
    { isError: isErrorEditUser, isLoading: isLoadingEditUser },
  ] = useEditUserMutation();

  useEffect(() => {
    const crumbs = [
      { name: "Home", route: "/" },
      { name: "Users", route: "/users" },
    ];
    dispatch(setBreadcrumb(crumbs));
  }, []);
  const [triggerSearch, { data: searchData, isLoading: isSearchLoading, isError: isSearchError }] = 
    useLazySearchUsersQuery();
  const [getUsers, { data, isLoading, isError }] = useLazyGetUsersQuery();
  const [deleteUser, { isLoading: isLoadingDeleteUser }] =
    useDeleteUserMutation();
  const [createUser, setCreateUser] = useState(false);
  const [editUser, setEditUser] = useState(false);
  const [userData, setUserData] = useState({
    email: "",
    role: "",
    givenName: "",
    familyName: "",
  });
  const [editedData, setEditedData] = useState({
    givenName: "",
    familyName: "",
    id: "",
    email: "",
    role: "",
  });

  const [rawData, setRawData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [roleValue, setRoleValue] = useState("All");
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState("");

  const [totalPages, setTotalPages] = useState(1);

  const [token, setToken] = useState([]);

  const [toggleSorting, setToggleSorting] = useState(false);
  const [deleteModal, setDeleteModal] = useState({
    open: false,
    id: "",
    email: "",
  });
  const { data: rolesAndPermissions, isLoading: isLoadingRoles } =
    useGetRolesQuery("");
  const [roles, setRoles] = useState([]);
  const isLoadingData = isLoading || isSearchLoading;

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm]);
  useEffect(() => {
    if (debouncedSearchTerm.length >= 3) {
      triggerSearch(debouncedSearchTerm);
      setCurrentPage(1); // Reset to first page when searching
    } else {
      setFilteredData(rawData);
    }
  }, [debouncedSearchTerm, triggerSearch, rawData]);

  useEffect(() => {
    const roleAliases = rolesAlias.reduce((acc, role) => {
      acc[role.value] = role.label;
      return acc;
    }, {});
  

    let dataSource;
    if (debouncedSearchTerm.length >= 3) {
      if (isSearchError) {
        dataSource = []; // Set to empty if there's an error
      } else {
        dataSource = searchData || [];
      }
    } else {
      dataSource = rawData;
    }
  
    // Filter data by selected role
    const filteredByRole =
      roleValue === "All"
        ? dataSource
        : dataSource.filter((item) => {
            const role = roleAliases[item.role] || "none";
            return role === roleValue;
          });
  
    setFilteredData(filteredByRole);
  }, [debouncedSearchTerm, rawData, roleValue, searchData, isSearchError]);

  useEffect(() => {
    const roles = rolesAndPermissions ? Object.keys(rolesAndPermissions) : [];
    setRoles(roles);
  }, [rolesAndPermissions]);

  useEffect(() => {
    getUsers("");
  }, []);

  const [limit, setLimit] = useState(20);

  useEffect(() => {
    if (currentPage === 1 && data?.users) {
      setLimit(data.users.length);
    }
  }, [data]);
  useEffect(() => {
    if (data) {
      setFilteredData(data.users || []);
      setRawData(data.users || []);
  
      setTotalPages(Math.ceil(data.count / limit));
      setToken((prevToken) => {
        const exists = prevToken.find(
          (item) =>
            item.page === currentPage || item.t === data.nextPaginationToken?.id
        );
        if (!exists) {
          return [
            ...prevToken,
            { page: currentPage, t: data.nextPaginationToken?.id || null },
          ];
        }
        return prevToken;
      });
    }
  }, [data, limit]);

  useEffect(() => {
    if (isError) {
      setFilteredData([]); // temp solution
    }
  }, [isError]);

  useEffect(() => {
    const roleAliases = rolesAlias.reduce((acc, role) => {
      acc[role.value] = role.label;
      return acc;
    }, {});

    const filteredByRole =
      roleValue === "All"
        ? rawData
        : rawData.filter((item) => {
            const role = roleAliases[item.role] || "none";
            return role === roleValue;
          });
    const filteredData =
      search === ""
        ? filteredByRole
        : filteredByRole.filter((item) => {
            const searchLower = search.toLowerCase();

            const userStatus = item.status.toLowerCase().includes("external")
              ? "External"
              : "Internal";
            return (
              item.givenName?.toLowerCase().includes(searchLower) ||
              item.familyName?.toLowerCase().includes(searchLower) ||
              item.email?.toLowerCase().includes(searchLower) ||
              item.role?.toLowerCase().includes(searchLower) ||
              userStatus?.toLowerCase().includes(searchLower)
            );
          });

    setFilteredData(filteredData);
  }, [search, rawData, roleValue]);

  const handleNext = () => {
    const nextpage = currentPage + 1;
    const currentToken = token.find((t) => t.page === currentPage)?.t;

    getUsers(currentToken);

    setCurrentPage(nextpage);
  };

  const handlePrev = () => {
    const prevPage = currentPage - 1;

    const currentToken = token.find((t) => t.page === prevPage - 1)?.t;

    getUsers(prevPage === 1 ? null : currentToken);

    setCurrentPage(prevPage);
  };

  const rolesAlias = userRoleOptions;
  const rows =
    filteredData.length > 0
      ? filteredData.map((item) => {
          const canManageUser = canManageUserRole(callerGroups, item.role);
          const userRole =
            rolesAlias.find((role) => role.value === item.role)?.label ||
            "none";
          const userStatus = item.status.toLowerCase().includes("external")
            ? "External"
            : "Internal";
          return (
            <Table.Tr key={item.id}>
              <Table.Td>{item.email}</Table.Td>
              <Table.Td className="flex items-center justify-center">
                <div
                  className={
                    "py-1 px-2 bg-actionLight text-action rounded-md w-fit text-center"
                  }
                >
                  {userRole}
                </div>
              </Table.Td>
              <Table.Td>{item.givenName}</Table.Td>
              <Table.Td>{item.familyName}</Table.Td>
              <Table.Td className="flex items-center justify-center">
                <div
                  style={{
                    background:
                      userStatus === "External" ? "#e5efff" : "#dcf7e3",
                    color: userStatus === "External" ? "#367bf5" : "#2fa84f",
                  }}
                  className={`rounded-md px-3 py-1 w-fit   capitalize  `}
                >
                  {userStatus}
                </div>
              </Table.Td>
              <Table.Td>{item.lastLogin}</Table.Td>
              <Table.Td>{item.createdAt}</Table.Td>

              <Table.Td style={{ padding: "0px", width: "95px" }}>
                <div className="flex gap-4 text-grayText2 items-center text-xl w-fit mx-auto px-3 lg:px-0">
                  {canManageUser && (
                    <PermissionAction operation="U" resource="users">
                      <div
                        className="cursor-pointer "
                        onClick={() => {
                          setEditUser(true);
                          setEditedData({
                            givenName: item?.givenName || "",
                            familyName: item?.familyName || "",
                            id: item.id,
                            email: item.email,
                            role: item.role,
                          });
                        }}
                      >
                        <EditIcon />
                      </div>
                    </PermissionAction>
                  )}
                  {canManageUser && (
                    <PermissionAction operation="D" resource="users">
                      <div
                        className="cursor-pointer"
                        onClick={() => {
                          setDeleteModal({
                            id: item.id,
                            open: true,
                            email: item.email,
                          });
                        }}
                      >
                        <TrashIcon />
                      </div>
                    </PermissionAction>
                  )}
                </div>
              </Table.Td>
            </Table.Tr>
          );
        })
      : [];
  const columnsLabels = [
    "Email",
    "Role",
    "First Name",
    "Last Name",
    "SSO",
    "Last Login",
    "Created At",
    "",
  ];

  const handleSortinByLastLogin = () => {
    const updated = [...filteredData].sort((a, b) =>
      toggleSorting ? a.lastLogin - b.lastLogin : b.lastLogin - a.lastLogin
    );

    setFilteredData(updated);
    setToggleSorting((val) => !val);
  };

  const columns = columnsLabels.map((column, index) => {
    const isFirstColumn = index === 0;
    const isLastColumn = index === columnsLabels.length - 1;
    const isCenterAligned = column === "Role" || column === "SSO";

    const thClassName = isFirstColumn
      ? "rounded-ss-[10px]"
      : isLastColumn
      ? "rounded-se-[10px]"
      : "";

    const divClassName = isCenterAligned
      ? "flex items-center justify-center"
      : "";

    return (
      <Table.Th key={index} className={thClassName}>
        <div className={divClassName}>{column}</div>
      </Table.Th>
    );
  });
  const handleDelete = async () => {
    try {
      await deleteUser(deleteModal.id).unwrap();
      setDeleteModal({ ...deleteModal, open: false });
      notifications.show({
        title: "Success",
        message: "User deleted successfully",
        color: "green",
      });
    } catch (err) {
      notifications.show({
        title: "Error",
        message: apiErrorMessage(err),
        color: "red",
      });
    }
  };
  const handleCreatUser = async (e) => {
    e.preventDefault();
    try {
      await createUsers(userData).unwrap();
      setCreateUser(false);
      notifications.show({
        title: "Success",
        message: "User invited successfully",
        color: "green",
      });
    } catch (err) {
      notifications.show({
        title: "Error",
        message: apiErrorMessage(err),
        color: "red",
      });
    }
  };

  const handleEditUser = async (e) => {
    e.preventDefault();
    try {
      await editUsers({
        content: {
          givenName: editedData.givenName,
          familyName: editedData.familyName,
          role: editedData.role,
        },
        id: editedData.id,
      }).unwrap();
      setEditUser(false);
      notifications.show({
        title: "Success",
        message: "User updated successfully",
        color: "green",
      });
    } catch (err) {
      notifications.show({
        title: "Error",
        message: apiErrorMessage(err),
        color: "red",
      });
    }
  };

  return (
    <PermissionAction operation="R" resource="users">
      <Container>
        <div className="flex flex-col min-[500px]:flex-row justify-between min-[500px]:items-center gap-4 w-full my-10">
          <div className=" flex gap-4 items-end ">
            <h1 className="font-[500] text-5xl"> Users</h1>
            <span className="text-grayText4 text-5xl">{data?.count}</span>
          </div>
          <PermissionAction operation="C" resource="users">
            <Button
              onClick={() => {
                setCreateUser(true);
                setUserData({
                  email: "",
                  role: "",
                  givenName: "",
                  familyName: "",
                });
              }}
              color={"#FB6514"}
              radius="xl"
              size="md"
              loading={isLoadingCreateUser}
            >
              <MdAdd size={20} />
              <span>Invite New User</span>
            </Button>
          </PermissionAction>
        </div>
        <div className="w-full flex flex-col sm:flex-row gap-4 justify-between sm:items-center">
          <SegmentedControl
            size="md"
            radius="xl"
            className="w-full sm:w-auto"
            value={roleValue}
            data={["All", ...userRoleOptions.map((role) => role.label)]}
            onChange={setRoleValue}
          />
          <div className="w-full sm:w-fit">
          <Input
      placeholder="Search"
      value={searchTerm}
      onChange={(e) => setSearchTerm(e.target.value)}
      leftSection={<IoSearchOutline />}
    />
          </div>
        </div>

        <div className=" relative mt-12  mb-32  border border-grayText4 border-solid rounded-[10px] ">
          <CustomTable
            rows={rows}
            columns={columns}
            isLoading={isLoadingData}
            isError={isError}
          />

          {rows.length !== 0 && !isError && (
            <div className="w-full flex gap-4 justify-between items-center py-4 px-6 border-top">
              <div className="flex gap-4 items-center">
                <button
                  disabled={currentPage === 1}
                  onClick={handlePrev}
                  className={`btn ${
                    currentPage === 1
                      ? "bg-[#F7F8F9] text-lightGray2 cursor-not-allowed"
                      : "bg-white text-grayText hover:bg-grayText4  "
                  }`}
                >
                  Previous
                </button>
                <button
                  onClick={handleNext}
                  disabled={currentPage === totalPages}
                  className={`btn ${
                    currentPage === totalPages
                      ? "bg-[#F7F8F9] text-lightGray2 cursor-not-allowed"
                      : "bg-white text-grayText hover:bg-grayText4"
                  }`}
                >
                  Next
                </button>
              </div>
              <div className="text-base text-grayText">
                Page {currentPage} of {totalPages}
              </div>
            </div>
          )}
        </div>

        <DeleteModal
          openModal={deleteModal.open}
          handleClose={() => setDeleteModal({ ...deleteModal, open: false })}
          targetName={""}
          desc={
            <p className="text-lg mt-2 mb-6 text-grayText5 text-center">
              This will permanently delete the user with address
              <span className="font-md px-1 text-grayText7">
                {deleteModal.email}
              </span>
              
            </p>
          }
          handleDelete={handleDelete}
          isLoading={isLoadingDeleteUser}
          isUsers={true}
        />
        <CreateUser
          setOpen={setCreateUser}
          open={createUser}
          setData={setUserData}
          data={userData}
          handleSubmit={handleCreatUser}
          roles={creatableRoles}
          type="create"
          isLoading={isLoadingCreateUser}
        />
        <CreateUser
          setOpen={setEditUser}
          open={editUser}
          setData={setEditedData}
          data={editedData}
          handleSubmit={handleEditUser}
          roles={creatableRoles}
          type="edit"
          isLoading={isLoadingEditUser}
        />
      </Container>
    </PermissionAction>
  );
};

export default Users;

const CreateUser = ({
  setOpen,
  open,
  setData,
  data,
  handleSubmit,
  roles,
  type,
  isLoading,
}) => {
  return (
    <Modal
      opened={open}
      onClose={() => setOpen(false)}
      centered
      size="lg"
      className="p-5"
      withCloseButton={false}
      radius={10}
    >
      <p className="text-2xl my-6  ">
        {type === "create" ? "Create New User " : "Edit User"}
      </p>
      <form onSubmit={handleSubmit}>
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <label>Email</label>
            <Input
              value={data.email}
              onChange={(e) => setData({ ...data, email: e.target.value })}
              placeholder="email"
              disabled={type === "edit"}
              type="email"
              required
            />
          </div>
          <div className="flex flex-col gap-2">
            <label>Role</label>
            <Select
              data={roles}
              value={data.role}
              onChange={(value) => setData({ ...data, role: value })}
              required
              allowDeselect={false}
            />
          </div>
          <div className="flex flex-col gap-2">
            <label>First Name</label>
            <Input
              value={data.givenName}
              onChange={(e) => setData({ ...data, givenName: e.target.value })}
            />
          </div>

          <div className="flex flex-col gap-2">
            <label>Last Name</label>
            <Input
              value={data.familyName}
              onChange={(e) => setData({ ...data, familyName: e.target.value })}
            />
          </div>

          <div className="flex gap-2 justify-end mt-8">
            <Button
              color="#D5DAE1"
              onClick={() => setOpen(false)}
              variant="default"
              size="md"
            >
              Cancel
            </Button>
            <Button color="#FB6514" size="md" type="submit" loading={isLoading}>
              {type === "create" ? "Create " : "Edit"}
            </Button>
          </div>
        </div>
      </form>
    </Modal>
  );
};
